// Yoga Shepton Mallet - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaSheptonMallet = () => {
  usePageMeta({
    title: "Yoga Classes Shepton Mallet | Annelie Lily",
    description: "Yoga classes for Shepton Mallet, Somerset. Small group Hatha & Yin yoga in Frome, 15 minutes via the A361. Personal attention, beginners welcome.",
    canonical: "https://annelielily.com/areas/yoga-shepton-mallet"
  });

  const locationSchema = generateLocationSchema({
    townName: "Shepton Mallet",
    description: "Yoga classes for Shepton Mallet at Annelie Lily Yoga in Frome. Small group classes and private sessions just 15 minutes via the A361.",
    url: "https://annelielily.com/yoga-shepton-mallet"
  });

  const faqs = [
    {
      question: "How do I get to Frome from Shepton Mallet?",
      answer: "Take the A361 south — it's a direct road and the journey takes about 15 minutes. Straightforward and pleasant."
    },
    {
      question: "What types of yoga do you offer?",
      answer: "I teach Hatha yoga (active practice with postures and breathwork), Yin yoga (slow, deep stretches), Aerial yoga (using a hammock), and private one-to-one sessions."
    },
    {
      question: "I'm quite stiff — will I be able to keep up?",
      answer: "Absolutely. Stiffness is common and yoga can help. I offer variations for every pose, so you work with your body as it is today. Many of my students started stiff and have gradually found more ease."
    },
    {
      question: "How much do classes cost?",
      answer: "Prices vary depending on the type of session. Get in touch and I'll send you current rates — I offer both drop-in classes and block bookings."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Shepton Mallet" currentSlug="areas/yoga-shepton-mallet" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Shepton Mallet</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Shepton Mallet</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Shepton Mallet and Frome are neighbours in the Somerset landscape — just 15 minutes 
              apart on the A361. Close enough for a yoga class that feels personal and unhurried.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Shepton Mallet</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Shepton Mallet Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              If you live in Shepton Mallet and you're looking for yoga, Frome is just down the road. 
              The A361 connects the two towns directly — no complicated route planning required.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              deliberately small, usually 6-8 people. This means I can actually pay attention to 
              how everyone's moving and offer guidance that's relevant to your body, not just 
              generic instructions.
            </p>
            <p>
              I offer both <Link to="/areas/yoga-shepton-mallet/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Shepton Mallet</Link> (more 
              active, building strength and flexibility) and <Link to="/areas/yoga-shepton-mallet/yin-yoga" className="text-gold hover:underline">Yin yoga in Shepton Mallet</Link> (slower, 
              deeper, meditative). <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Complete beginners</Link> are 
              welcome — many of my students started knowing nothing about yoga.
            </p>
            <p>
              The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Shepton Mallet</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga Shepton Mallet", desc: "Traditional practice combining postures, breathing, and relaxation. Good for building strength and flexibility.", link: "/areas/yoga-shepton-mallet/hatha-yoga" },
              { title: "Yin Yoga Shepton Mallet", desc: "Slow, meditative stretches held for several minutes. Brilliant for releasing tension and calming the nervous system.", link: "/areas/yoga-shepton-mallet/yin-yoga" },
              { title: "Private Sessions Shepton Mallet", desc: "One-to-one yoga tailored entirely to your needs and goals.", link: "/areas/yoga-shepton-mallet/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Shepton Mallet Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaSheptonMallet;
